import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CalendarEventCollection } from '../interface/collection';

declare const Meteor: any;

// Calendar event model
export interface CalendarEvent {
  id: string;
  _id?: string;
  userId?: string;
  title: string;
  startDate: Date;
  endDate: Date;
  allDay?: boolean;
  color?: CalendarEventColor;
  description?: string;
  location?: string;
  reminder?: number; // minutes before event
  recurrence?: EventRecurrence;
  category?: string;
}

export interface EventRecurrence {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  endDate?: Date;
  count?: number;
}

export interface CalendarEventColor {
  background: string;
  foreground: string;
}

// Predefined event colors/categories
export const EVENT_CATEGORIES = [
  { id: 'work', name: 'Work', color: { background: '#3b82f6', foreground: '#ffffff' } },
  { id: 'personal', name: 'Personal', color: { background: '#10b981', foreground: '#ffffff' } },
  { id: 'meeting', name: 'Meeting', color: { background: '#8b5cf6', foreground: '#ffffff' } },
  { id: 'important', name: 'Important', color: { background: '#ef4444', foreground: '#ffffff' } },
  { id: 'reminder', name: 'Reminder', color: { background: '#f59e0b', foreground: '#ffffff' } },
  { id: 'holiday', name: 'Holiday', color: { background: '#ec4899', foreground: '#ffffff' } },
  { id: 'birthday', name: 'Birthday', color: { background: '#06b6d4', foreground: '#ffffff' } },
  { id: 'other', name: 'Other', color: { background: '#6b7280', foreground: '#ffffff' } },
];

// Reminder options in minutes
export const REMINDER_OPTIONS = [
  { value: 0, label: 'At time of event' },
  { value: 5, label: '5 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 10080, label: '1 week before' },
];

const EVENTS_STORAGE_KEY = 'ews_calendar_events';

@Injectable({
  providedIn: 'root'
})
export class CalendarService implements OnDestroy {
  private eventsSubject = new BehaviorSubject<CalendarEvent[]>([]);
  public events$ = this.eventsSubject.asObservable();
  
  private destroy$ = new Subject<void>();
  private userId: string = '';

  // Signals for reactive state
  readonly selectedDate = signal<Date>(new Date());
  readonly currentView = signal<'month' | 'week' | 'list'>('month');
  readonly isLoading = signal<boolean>(false);
  
  // Computed values
  readonly currentMonth = computed(() => {
    const date = this.selectedDate();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  readonly eventsForSelectedDate = computed(() => {
    const date = this.selectedDate();
    const events = this.eventsSubject.value;
    return events.filter(event => {
      const eventStart = new Date(event.startDate);
      return eventStart.toDateString() === date.toDateString();
    });
  });

  constructor() {
    this.loadEventsFromStorage();
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.loadEventsFromMeteor();
  }

  // Load events from Meteor subscription and method
  private loadEventsFromMeteor(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    this.isLoading.set(true);
    
    try {
      // First, try to get events via the method call for initial load
      Meteor.call('getCalendarEvents', this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error loading calendar events:', error);
          this.isLoading.set(false);
          return;
        }
        
        if (result && result.code === 200 && result.data) {
          const mappedEvents = result.data.map((event: any) => this.mapMeteorEvent(event));
          this.eventsSubject.next(mappedEvents);
          this.saveEventsToStorage(mappedEvents);
        }
        this.isLoading.set(false);
      });

      // Also set up reactive subscription for real-time updates
      Meteor.autorun(() => {
        const state = Meteor.subscribe('calendarEvents', this.userId);
        if (state.ready()) {
          const events = CalendarEventCollection.find({ userId: this.userId }).fetch();
          const mappedEvents = events.map((event: any) => this.mapMeteorEvent(event));
          this.eventsSubject.next(mappedEvents);
          this.saveEventsToStorage(mappedEvents);
        }
      });
    } catch (error) {
      console.error('Error loading calendar events from Meteor:', error);
      this.isLoading.set(false);
    }
  }

  // Load events from local storage (fallback)
  private loadEventsFromStorage(): void {
    try {
      const stored = localStorage.getItem(EVENTS_STORAGE_KEY);
      if (stored) {
        const events = JSON.parse(stored).map((event: any) => ({
          ...event,
          startDate: new Date(event.startDate),
          endDate: new Date(event.endDate),
        }));
        this.eventsSubject.next(events);
      }
    } catch (error) {
      console.error('Error loading calendar events from storage:', error);
    }
  }

  private saveEventsToStorage(events: CalendarEvent[]): void {
    try {
      localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(events));
    } catch (error) {
      console.error('Error saving calendar events to storage:', error);
    }
  }

  private mapMeteorEvent(event: any): CalendarEvent {
    return {
      id: event._id,
      _id: event._id,
      userId: event.userId,
      title: event.title || event.summary || '', // Support both title and summary from backend
      startDate: new Date(event.startDate),
      endDate: new Date(event.endDate),
      allDay: event.allDay || false,
      color: event.color || EVENT_CATEGORIES[0].color,
      description: event.description || '',
      location: event.location || '',
      reminder: event.reminder,
      recurrence: event.recurrence,
      category: event.category || 'work',
    };
  }

  get events(): CalendarEvent[] {
    return this.eventsSubject.value;
  }

  // Add a new event
  addEvent(event: Omit<CalendarEvent, 'id'>): Observable<CalendarEvent> {
    return new Observable(observer => {
      const newEvent: CalendarEvent = {
        ...event,
        id: this.generateId(),
        userId: this.userId,
      };

      if (typeof Meteor !== 'undefined' && this.userId) {
        // Map frontend model to backend model (title -> summary)
        const meteorEvent = {
          userId: this.userId,
          summary: newEvent.title,
          title: newEvent.title,
          description: newEvent.description || '',
          startDate: newEvent.startDate,
          endDate: newEvent.endDate,
          allDay: newEvent.allDay || false,
          color: newEvent.color || EVENT_CATEGORIES[0].color,
          location: newEvent.location || '',
          reminder: newEvent.reminder,
          recurrence: newEvent.recurrence,
          category: newEvent.category || 'work',
        };

        Meteor.call('createCalendarEvent', meteorEvent, (error: any, result: any) => {
          if (error) {
            console.error('Error adding calendar event:', error);
            // Still add locally as fallback
            this.addEventLocally(newEvent);
            observer.next(newEvent);
          } else if (result.code === 201) {
            newEvent._id = result.insertedId;
            newEvent.id = result.insertedId;
            this.addEventLocally(newEvent);
            observer.next(newEvent);
          } else {
            console.error('Error adding calendar event:', result.message);
            this.addEventLocally(newEvent);
            observer.next(newEvent);
          }
          observer.complete();
        });
      } else {
        this.addEventLocally(newEvent);
        observer.next(newEvent);
        observer.complete();
      }
    });
  }

  private addEventLocally(event: CalendarEvent): void {
    const events = [...this.eventsSubject.value, event];
    this.eventsSubject.next(events);
    this.saveEventsToStorage(events);
  }

  // Update an existing event
  updateEvent(id: string, updates: Partial<CalendarEvent>): Observable<CalendarEvent | null> {
    return new Observable(observer => {
      const events = this.eventsSubject.value;
      const index = events.findIndex(e => e.id === id || e._id === id);
      
      if (index === -1) {
        observer.next(null);
        observer.complete();
        return;
      }

      const updatedEvent = { ...events[index], ...updates };

      if (typeof Meteor !== 'undefined' && this.userId) {
        // Map frontend model to backend model (title -> summary)
        const meteorUpdates: any = { ...updates };
        if (updates.title) {
          meteorUpdates.summary = updates.title;
        }

        Meteor.call('updateCalendarEvent', id, meteorUpdates, (error: any, result: any) => {
          if (error) {
            console.error('Error updating calendar event:', error);
          } else if (result && result.code !== 200) {
            console.error('Error updating calendar event:', result.message);
          }
          this.updateEventLocally(index, updatedEvent);
          observer.next(updatedEvent);
          observer.complete();
        });
      } else {
        this.updateEventLocally(index, updatedEvent);
        observer.next(updatedEvent);
        observer.complete();
      }
    });
  }

  private updateEventLocally(index: number, event: CalendarEvent): void {
    const events = [...this.eventsSubject.value];
    events[index] = event;
    this.eventsSubject.next(events);
    this.saveEventsToStorage(events);
  }

  // Delete an event
  deleteEvent(id: string): Observable<boolean> {
    return new Observable(observer => {
      if (typeof Meteor !== 'undefined' && this.userId) {
        // Backend requires both eventId and userId
        Meteor.call('deleteCalendarEvent', id, this.userId, (error: any, result: any) => {
          if (error) {
            console.error('Error deleting calendar event:', error);
          } else if (result && result.code !== 200) {
            console.error('Error deleting calendar event:', result.message);
          }
          this.deleteEventLocally(id);
          observer.next(true);
          observer.complete();
        });
      } else {
        this.deleteEventLocally(id);
        observer.next(true);
        observer.complete();
      }
    });
  }

  private deleteEventLocally(id: string): void {
    const events = this.eventsSubject.value.filter(e => e.id !== id && e._id !== id);
    this.eventsSubject.next(events);
    this.saveEventsToStorage(events);
  }

  // Get events for a specific date range
  getEventsInRange(startDate: Date, endDate: Date): CalendarEvent[] {
    return this.eventsSubject.value.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      return (eventStart >= startDate && eventStart <= endDate) ||
             (eventEnd >= startDate && eventEnd <= endDate) ||
             (eventStart <= startDate && eventEnd >= endDate);
    });
  }

  // Get events for a specific date
  getEventsForDate(date: Date): CalendarEvent[] {
    return this.eventsSubject.value.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      const targetEndOfDay = new Date(date);
      targetEndOfDay.setHours(23, 59, 59, 999);
      
      return (eventStart <= targetEndOfDay && eventEnd >= targetDate);
    });
  }

  // Get upcoming events
  getUpcomingEvents(limit: number = 10): CalendarEvent[] {
    const now = new Date();
    return this.eventsSubject.value
      .filter(event => new Date(event.startDate) >= now)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .slice(0, limit);
  }

  // Navigation methods
  goToDate(date: Date): void {
    this.selectedDate.set(date);
  }

  goToToday(): void {
    this.selectedDate.set(new Date());
  }

  goToNextMonth(): void {
    const current = this.selectedDate();
    this.selectedDate.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  }

  goToPreviousMonth(): void {
    const current = this.selectedDate();
    this.selectedDate.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  }

  goToNextWeek(): void {
    const current = this.selectedDate();
    const nextWeek = new Date(current);
    nextWeek.setDate(nextWeek.getDate() + 7);
    this.selectedDate.set(nextWeek);
  }

  goToPreviousWeek(): void {
    const current = this.selectedDate();
    const prevWeek = new Date(current);
    prevWeek.setDate(prevWeek.getDate() - 7);
    this.selectedDate.set(prevWeek);
  }

  setView(view: 'month' | 'week' | 'list'): void {
    this.currentView.set(view);
  }

  // Helper method to generate unique IDs
  private generateId(): string {
    return 'evt_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  // Get category color
  getCategoryColor(categoryId: string): CalendarEventColor {
    const category = EVENT_CATEGORIES.find(c => c.id === categoryId);
    return category?.color || EVENT_CATEGORIES[0].color;
  }

  // Export events to JSON
  exportEvents(): string {
    return JSON.stringify(this.eventsSubject.value, null, 2);
  }

  // Import events from JSON
  importEvents(json: string): boolean {
    try {
      const imported = JSON.parse(json);
      if (Array.isArray(imported)) {
        const events = imported.map((event: any) => ({
          ...event,
          id: event.id || this.generateId(),
          startDate: new Date(event.startDate),
          endDate: new Date(event.endDate),
        }));
        
        // Merge with existing events
        const existingIds = new Set(this.eventsSubject.value.map(e => e.id));
        const newEvents = events.filter((e: CalendarEvent) => !existingIds.has(e.id));
        const mergedEvents = [...this.eventsSubject.value, ...newEvents];
        
        this.eventsSubject.next(mergedEvents);
        this.saveEventsToStorage(mergedEvents);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importing calendar events:', error);
      return false;
    }
  }

  // Clear all events
  clearAllEvents(): void {
    this.eventsSubject.next([]);
    this.saveEventsToStorage([]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

